import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getCurrentAIAllowance } from "../api/aiAllowance";
import { getCustomers } from "../api/customers";
import { getDocuments } from "../api/documents";
import { getApiError } from "../api/errors";
import { getInventory } from "../api/inventory";
import { getProducts } from "../api/products";
import { getQuotations } from "../api/quotations";
import { getSalesReportSummary } from "../api/reports";
import { getStoreBridgeDevices } from "../api/storeBridge";
import { getCurrentWhatsAppConnection } from "../api/whatsapp";
import PageHeader from "../components/PageHeader";
import { LoadingPanel } from "../components/PageState";
import SetupChecklist from "../components/SetupChecklist";
import { useAIAssistant } from "../context/AIAssistantContext";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatMoney } from "../utils/format";
import { HARDWARE_READINESS_EVENT, getPrinterReadiness, getScannerReadiness } from "../utils/hardwareReadiness";
import { STORE_BRIDGE_CHANGED_EVENT, getSelectedStoreBridgeDeviceId } from "../utils/storeBridge";

const initialData = {
  productTotal: 0,
  inventoryTotal: 0,
  lowStockTotal: 0,
  customerTotal: 0,
  pendingQuotationTotal: 0,
  documents: [],
  whatsapp: null,
  aiAllowance: null,
  dailySales: null,
};

function todayValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function MetricCard({ label, value, helper, icon: Icon, tone = "blue", path }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tones = {
    blue: { bg: "#eff6ff", color: "#2563eb" },
    violet: { bg: "#f5f3ff", color: "#7c3aed" },
    amber: { bg: "#fff7ed", color: "#d97706" },
    green: { bg: "#f0fdf4", color: "#16a34a" },
  };
  const colors = tones[tone] || tones.blue;

  const content = (
      <CardContent sx={{ p: 2.65 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box>
            <Typography color="text.secondary" variant="body2" fontWeight={800}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, overflowWrap: "anywhere" }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              bgcolor: colors.bg,
              color: colors.color,
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.35 }}>
          {helper}
        </Typography>
        {path && <Typography variant="body2" fontWeight={800} sx={{ mt: 1, color: colors.color }}>{t("dashboard.viewInventory")}</Typography>}
      </CardContent>
  );
  return (
    <Card sx={{ height: "100%", borderRadius: 4, ...(path && { transition: "box-shadow 160ms ease, transform 160ms ease", "&:hover": { boxShadow: 6, transform: "translateY(-2px)" } }) }}>
      {path ? <CardActionArea aria-label={t("dashboard.viewLabel", { label })} onClick={() => navigate(path)} sx={{ height: "100%", cursor: "pointer" }}>{content}</CardActionArea> : content}
    </Card>
  );
}

function QuickAction({ title, description, icon: Icon, path, tone = "#eff6ff" }) {
  const navigate = useNavigate();
  return (
    <Card sx={{ height: "100%", borderRadius: 4, boxShadow: "none" }}>
      <CardActionArea onClick={() => navigate(path)} sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: tone, display: "grid", placeItems: "center" }}
            >
              <Icon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={900}>{title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.55 }}>
                {description}
              </Typography>
            </Box>
            <ArrowForwardRoundedIcon sx={{ color: "#94a3b8" }} fontSize="small" />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { selectedWorkspace, selectedStore, canManage, role, isSystemAccount } = useAuth();
  const { openAssistant } = useAIAssistant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(initialData);
  const [scannerReady, setScannerReady] = useState(false);
  const [printerReady, setPrinterReady] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    if (!selectedWorkspace?.public_id || !selectedStore?.public_id) {
      setData(initialData);
      setLoading(false);
      return;
    }

    try {
      const [products, inventory, documents, customers, quotations, allowance] = await Promise.all([
        getProducts({ page: 1, page_size: 1 }),
        getInventory({ page: 1, page_size: 1 }),
        getDocuments(),
        getCustomers({ page: 1, page_size: 1 }),
        getQuotations({ status: "pending_approval", page: 1, page_size: 1 }),
        getCurrentAIAllowance().catch(() => null),
      ]);

      let whatsapp = null;
      let dailySales = null;
      if (canManage) {
        const [whatsappResult, salesResult] = await Promise.allSettled([
          getCurrentWhatsAppConnection(),
          role === "owner"
            ? getSalesReportSummary({ period: "daily", anchorDate: todayValue(), topLimit: 1 })
            : Promise.resolve(null),
        ]);
        if (whatsappResult.status === "fulfilled") {
          whatsapp = whatsappResult.value?.data || null;
        }
        if (salesResult.status === "fulfilled") {
          dailySales = salesResult.value;
        }
      }

      setData({
        productTotal: Number(products.data?.total || 0),
        inventoryTotal: Number(inventory.data?.total || 0),
        lowStockTotal: Number(inventory.data?.low_stock_total || 0),
        documents: Array.isArray(documents.data) ? documents.data : [],
        customerTotal: Number(customers.data?.total || 0),
        pendingQuotationTotal: Number(quotations.data?.total || 0),
        whatsapp,
        aiAllowance: allowance,
        dailySales,
      });
    } catch (requestError) {
      setError(getApiError(requestError, t("dashboard.loadError")));
    } finally {
      setLoading(false);
    }
  }, [canManage, role, selectedStore?.public_id, selectedWorkspace?.public_id, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const workspaceId = selectedWorkspace?.public_id;
    const storeId = selectedStore?.public_id;
    let active = true;

    const refreshHardwareReadiness = async () => {
      setScannerReady(Boolean(getScannerReadiness(workspaceId, storeId)));
      const browserPrinterReady = Boolean(getPrinterReadiness(workspaceId, storeId));
      const selectedBridgeId = getSelectedStoreBridgeDeviceId(workspaceId, storeId);
      if (!selectedBridgeId) {
        setPrinterReady(browserPrinterReady);
        return;
      }
      try {
        const devices = await getStoreBridgeDevices();
        if (!active) return;
        const bridge = devices.find((item) => item.device_id === selectedBridgeId);
        const bridgePrinterReady = Boolean(
          bridge
          && bridge.status === "active"
          && bridge.online
          && bridge.settings?.printer_name,
        );
        setPrinterReady(browserPrinterReady || bridgePrinterReady);
      } catch {
        if (active) setPrinterReady(browserPrinterReady);
      }
    };

    refreshHardwareReadiness();
    window.addEventListener(HARDWARE_READINESS_EVENT, refreshHardwareReadiness);
    window.addEventListener(STORE_BRIDGE_CHANGED_EVENT, refreshHardwareReadiness);
    window.addEventListener("storage", refreshHardwareReadiness);
    return () => {
      active = false;
      window.removeEventListener(HARDWARE_READINESS_EVENT, refreshHardwareReadiness);
      window.removeEventListener(STORE_BRIDGE_CHANGED_EVENT, refreshHardwareReadiness);
      window.removeEventListener("storage", refreshHardwareReadiness);
    };
  }, [selectedStore?.public_id, selectedWorkspace?.public_id]);

  const whatsappConnected = data.whatsapp?.status === "connected";

  const setupSteps = useMemo(() => {
    const steps = [
      {
        label: t("dashboard.setupProducts"),
        description: t("dashboard.setupProductsHelp"),
        complete: data.productTotal > 0,
        path: "/products",
      },
      {
        label: t("dashboard.setupInventory"),
        description: t("dashboard.setupInventoryHelp"),
        complete: data.inventoryTotal > 0,
        path: "/inventory",
      },
      {
        label: t("dashboard.setupScanner"),
        description: t("dashboard.setupScannerHelp"),
        complete: scannerReady,
        path: "/hardware",
      },
      {
        label: t("dashboard.setupPrinter"),
        description: t("dashboard.setupPrinterHelp"),
        complete: printerReady,
        path: "/hardware",
      },
      {
        label: t("dashboard.setupKnowledge"),
        description: t("dashboard.setupKnowledgeHelp"),
        complete: data.documents.length > 0,
        path: "/documents",
      },
    ];

    if (canManage) {
      steps.push(
        {
          label: t("dashboard.setupImport"),
          description: t("dashboard.setupImportHelp"),
          complete: data.productTotal > 0 && data.inventoryTotal > 0,
          path: "/store-import",
        },
        {
          label: t("dashboard.setupWhatsApp"),
          description: t("dashboard.setupWhatsAppHelp"),
          complete: whatsappConnected,
          path: "/whatsapp",
        },
      );
    }
    return steps;
  }, [canManage, data.documents.length, data.inventoryTotal, data.productTotal, printerReady, scannerReady, whatsappConnected, t]);

  return (
    <MainLayout>
      <PageHeader
        eyebrow={t("dashboard.storeOverview")}
        title={t("dashboard.welcome", { store: selectedStore?.name || t("dashboard.yourStore") })}
        description={t("dashboard.subtitle", { workspace: selectedWorkspace?.name || t("dashboard.yourWorkspace") })}
        status={{ label: t("dashboard.online"), color: "success", variant: "outlined", sx: { bgcolor: "#f0fdf4" } }}
      />

      {isSystemAccount && <Alert severity="info" sx={{ mb: 3 }}>{t("dashboard.systemAccess")}</Alert>}
      {!isSystemAccount && selectedWorkspace?.days_remaining > 0 && selectedWorkspace.days_remaining <= 5 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t("dashboard.subscription", { days: selectedWorkspace.days_remaining })}
        </Alert>
      )}

      {!isSystemAccount && data.aiAllowance && ["warning", "critical", "exhausted"].includes(data.aiAllowance.severity) && (
        <Alert
          severity={data.aiAllowance.severity === "warning" ? "warning" : "error"}
          sx={{ mb: 3 }}
        >
          {t("dashboard.aiUsage", {
            percent: Number(data.aiAllowance.usage_percent || 0).toFixed(1),
            remaining: data.aiAllowance.remaining_operations !== null
              ? t("dashboard.remainingOps", { count: Number(data.aiAllowance.remaining_operations).toLocaleString("en-US") })
              : ".",
          })}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" onClick={load}>{t("common.retry")}</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingPanel label={t("dashboard.loading")} />
      ) : (
        <>
          <Grid container spacing={2.25}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard label={t("nav.products")} value={data.productTotal} helper={t("dashboard.catalogItems")} icon={Inventory2RoundedIcon} tone="blue" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label={t("dashboard.lowStock")}
                value={data.lowStockTotal}
                helper={data.lowStockTotal ? t("dashboard.needsAttention", { count: data.lowStockTotal }) : t("dashboard.healthy")}
                icon={WarehouseRoundedIcon}
                tone={data.lowStockTotal ? "amber" : "green"}
                path="/inventory?status=low-stock"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard label={t("nav.customers")} value={data.customerTotal} helper={t("dashboard.customerHistory")} icon={PeopleAltRoundedIcon} tone="violet" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label={t("dashboard.pendingQuotations")}
                value={data.pendingQuotationTotal}
                helper={t("dashboard.managementReview")}
                icon={RequestQuoteRoundedIcon}
                tone="amber"
              />
            </Grid>
            {canManage && data.dailySales && (
              <>
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    label={t("dashboard.netSales")}
                    value={formatMoney(data.dailySales.net_sales, "USD")}
                    helper={t("dashboard.grossBeforeRefunds", { amount: formatMoney(data.dailySales.gross_sales, "USD") })}
                    icon={AttachMoneyRoundedIcon}
                    tone="green"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <MetricCard
                    label={t("dashboard.transactions")}
                    value={Number(data.dailySales.transaction_count || 0).toLocaleString()}
                    helper={t("dashboard.netUnits", { count: Number(data.dailySales.net_units || 0).toLocaleString("en-US") })}
                    icon={PointOfSaleRoundedIcon}
                    tone="blue"
                  />
                </Grid>
              </>
            )}
            {!isSystemAccount && data.aiAllowance && (
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  label={t("dashboard.aiOperations")}
                  value={data.aiAllowance.used_operations}
                  helper={data.aiAllowance.operation_limit !== null
                    ? t("dashboard.operationsRemaining", { remaining: Number(data.aiAllowance.remaining_operations || 0).toLocaleString("en-US"), limit: Number(data.aiAllowance.operation_limit).toLocaleString("en-US"), topUp: Number(data.aiAllowance.top_up_operations || 0) > 0 ? t("dashboard.topUp", { count: Number(data.aiAllowance.top_up_operations).toLocaleString("en-US") }) : "", date: new Date(data.aiAllowance.period_end).toLocaleDateString("en-GB") })
                    : t("dashboard.unlimitedOperations")}
                  icon={AutoAwesomeRoundedIcon}
                  tone={data.aiAllowance.severity === "normal" || data.aiAllowance.severity === "unlimited" ? "green" : "amber"}
                />
              </Grid>
            )}
            {!isSystemAccount && data.aiAllowance && (
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  label={t("dashboard.voiceUsage")}
                  value={t("dashboard.minutes", { count: Number(data.aiAllowance.used_voice_minutes || 0).toLocaleString("en-US") })}
                  helper={data.aiAllowance.voice_limit_minutes !== null
                    ? t("dashboard.voiceRemaining", { count: Number(data.aiAllowance.remaining_voice_minutes || 0).toLocaleString("en-US"), date: new Date(data.aiAllowance.period_end).toLocaleDateString("en-GB") })
                    : t("dashboard.unlimitedVoice")}
                  icon={ForumRoundedIcon}
                  tone={data.aiAllowance.voice_severity === "normal" || data.aiAllowance.voice_severity === "unlimited" ? "green" : "amber"}
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2.25} sx={{ mt: 0.1 }}>
            <Grid item xs={12} lg={6}>
              <SetupChecklist steps={setupSteps} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%", borderRadius: 4, bgcolor: "#0f172a", color: "white", overflow: "hidden" }}>
                <CardContent sx={{ p: { xs: 2.75, md: 3.25 }, position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      width: 220,
                      height: 220,
                      borderRadius: "50%",
                      bgcolor: "rgba(59,130,246,0.18)",
                      right: -80,
                      top: -100,
                    }}
                  />
                  <AutoAwesomeRoundedIcon sx={{ color: "#7dd3fc", fontSize: 34 }} />
                  <Typography variant="h5" sx={{ mt: 1.5, color: "white" }}>
                    {t("dashboard.aiReady")}
                  </Typography>
                  <Typography sx={{ color: "#cbd5e1", mt: 1, lineHeight: 1.7, maxWidth: 560 }}>
                    {t("dashboard.aiReadyHelp")}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={openAssistant}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{ mt: 2.5 }}
                  >
                    {t("dashboard.openAi")}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box>
                <Typography variant="h6">{t("dashboard.quickActions")}</Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 0.35 }}>
                  {t("dashboard.quickHelp")}
                </Typography>
              </Box>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <QuickAction title={t("dashboard.reviewConversations")} description={t("dashboard.reviewConversationsHelp")} icon={ForumRoundedIcon} path="/conversations" tone="#eff6ff" />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <QuickAction title={t("dashboard.updateInventory")} description={t("dashboard.updateInventoryHelp")} icon={WarehouseRoundedIcon} path="/inventory" tone="#f0fdf4" />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <QuickAction title={t("dashboard.manageKnowledge")} description={t("dashboard.manageKnowledgeHelp")} icon={DescriptionRoundedIcon} path="/documents" tone="#f5f3ff" />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <QuickAction
                  title={t(canManage ? (data.productTotal > 0 && data.inventoryTotal > 0 ? "dashboard.checkWhatsApp" : "dashboard.importData") : "dashboard.createQuotation")}
                  description={t(canManage ? (data.productTotal > 0 && data.inventoryTotal > 0 ? "dashboard.checkWhatsAppHelp" : "dashboard.importDataHelp") : "dashboard.createQuotationHelp")}
                  icon={canManage ? (data.productTotal > 0 && data.inventoryTotal > 0 ? WhatsAppIcon : UploadFileRoundedIcon) : RequestQuoteRoundedIcon}
                  path={canManage ? (data.productTotal > 0 && data.inventoryTotal > 0 ? "/whatsapp" : "/store-import") : "/quotations"}
                  tone="#fff7ed"
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </MainLayout>
  );
}

